import React, { useCallback, useMemo } from "react";
import { Box, Typography } from "@material-ui/core";
import { ResponsiveBoxPlot } from "@nivo/boxplot";
import {
  FALLBACK_COLORS,
  REACH_COLORS,
  buildAriaLabel,
  buildMarkers,
  computeYDomain,
  fmt,
} from "./usdsBoxPlotHelpers";
import {
  landmarkRows,
  makeLandmarksLayer,
  makeSamplesLayer,
} from "./usdsBoxPlotLayers";
import { SMALL_SAMPLE_LIMIT } from "../../../pages/upstreamDownstream/usdsParams";

/**
 * One reach of the upstream-to-downstream box plots.
 *
 * Sites run left to right in upstream-to-downstream order, which is fixed by
 * passing `groups` rather than letting Nivo infer it from the data. Each site
 * carries a dark box for the selected water year and a light box for the
 * comparison period.
 *
 * Nivo computes the box from the raw values using the `quantiles` prop, so the
 * whiskers it draws are the same 5th and 95th percentiles the SQL reports, and
 * the y axis is sized from those same percentiles so a whisker can never run
 * off the top. The fences and outlier flags that come back from the API are
 * used for the tooltip and the footnotes, not for the drawing.
 *
 * `domain` is the shared axis when the page locks every reach to one scale;
 * when it is null each reach sizes its own axis from its own boxes.
 */

const chartTheme = {
  axis: {
    ticks: { text: { fontSize: 12 } },
    legend: { text: { fontSize: 13, fontWeight: 600 } },
  },
  legends: { text: { fontSize: 12 } },
  grid: { line: { stroke: "#eceff1" } },
};

const tooltipStyles = {
  backgroundColor: "#fff",
  border: "1px solid #999",
  borderRadius: 6,
  padding: 10,
  fontSize: 13,
  lineHeight: 1.5,
  color: "#333",
};

export default function UsdsBoxPlotChart({
  reach,
  parameter,
  selectedLabel,
  domain,
  periodColors,
  hideBenchmark = false,
  landmarks = [],
  scale = "linear",
  height = 420,
  quantiles = [0.05, 0.25, 0.5, 0.75, 0.95],
}) {
  const [dark, light] = REACH_COLORS[reach.reachGroupid] ?? FALLBACK_COLORS;

  /* A function, not an array. Nivo turns an array into a d3 ordinal scale with
     an implicit domain, memoized on the array's identity, so a module constant
     palette kept one scale alive across renders and every newly selected year
     appended to its domain and cycled the range: dark, light, dark, light. A
     function is used as-is and has no state. The first sub group is always the
     selected year (the hook sorts it there). */
  const getColor = useCallback(
    (d) =>
      periodColors?.get(d.subGroup) ??
      (d.subGroup === reach.subGroups[0] ? dark : light),
    [periodColors, reach.subGroups, dark, light]
  );

  const allMarkers = useMemo(
    () => (hideBenchmark ? [] : buildMarkers(parameter)),
    [parameter, hideBenchmark]
  );
  const ariaLabel = useMemo(
    () => buildAriaLabel(reach, parameter, selectedLabel),
    [reach, parameter, selectedLabel]
  );

  const unit =
    parameter?.unitName && parameter.unitName !== "None"
      ? parameter.unitName
      : "";

  const { min: minValue, max: maxValue } = useMemo(
    () =>
      computeYDomain(
        domain ?? reach.dataDomain,
        hideBenchmark ? null : parameter
      ),
    [domain, reach.dataDomain, parameter, hideBenchmark]
  );

  /* A marker outside the axis would be drawn past the plot area, so only the
     benchmarks the axis reaches are passed; the container names the rest. */
  const markers = useMemo(
    () =>
      allMarkers.filter(
        (m) =>
          minValue === "auto" || (m.value >= minValue && m.value <= maxValue)
      ),
    [allMarkers, minValue, maxValue]
  );

  const LandmarksLayer = useMemo(
    () => makeLandmarksLayer(landmarks),
    [landmarks]
  );
  const SamplesLayer = useMemo(
    () => makeSamplesLayer(reach.boxesByKey, SMALL_SAMPLE_LIMIT),
    [reach.boxesByKey]
  );

  const renderTooltip = ({ label, formatted, color }) => {
    const [site, period] = String(label).split(" - ");
    const box = reach.boxesByKey?.get(`${site}|${period}`);
    return (
      <Box style={tooltipStyles}>
        <div>
          <strong>{site}</strong>
          {box?.isSynoptic ? " (synoptic site)" : ""}
        </div>
        <div style={{ color }}>
          {period}
          {box?.isComparison &&
          `WY ${box.waterYearMin} to ${box.waterYearMax}` !== period
            ? ` (this site: WY ${box.waterYearMin} to ${box.waterYearMax})`
            : ""}
        </div>
        <div>
          n = {formatted?.n ?? box?.recordcount ?? "--"}
          {box?.porStart && box?.porEnd
            ? `, ${box.porStart} to ${box.porEnd}`
            : ""}
        </div>
        <div>
          Median {fmt(box?.median)} {unit}
        </div>
        <div>
          25th to 75th: {fmt(box?.pctl25)} to {fmt(box?.pctl75)} {unit}
        </div>
        <div>
          Range: {fmt(box?.minval)} to {fmt(box?.maxval)} {unit}
        </div>
        {box?.nondetectCount > 0 && (
          <div>
            Non-detects: {box.nondetectCount} of {box.recordcount} (plotted at
            zero)
          </div>
        )}
      </Box>
    );
  };

  if (!reach.values.length) {
    return (
      <Box
        height={height}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="body2" color="textSecondary">
          No results for {reach.reachLabel} in this period.
        </Typography>
      </Box>
    );
  }

  return (
    <Box height={height} role="img" aria-label={ariaLabel}>
      <ResponsiveBoxPlot
        data={reach.values}
        value="value"
        groupBy="site"
        groups={reach.groups}
        subGroupBy="period"
        subGroups={reach.subGroups}
        quantiles={quantiles}
        minValue={minValue}
        maxValue={maxValue}
        /* symlog rather than log: it is linear through zero, so non-detects
           stored as zero still have a place on the axis. */
        valueScale={{ type: scale === "log" ? "symlog" : "linear" }}
        layers={[
          "grid",
          LandmarksLayer,
          "axes",
          "boxPlots",
          SamplesLayer,
          "markers",
          "annotations",
        ]}
        /* Room in the top gutter for the landmark labels, one row of 14px per
           row they pack into. */
        margin={{
          top: 20 + 14 * landmarkRows(landmarks),
          right: 24,
          bottom: 80,
          left: 72,
        }}
        padding={0.25}
        innerPadding={4}
        colors={getColor}
        colorBy="subGroup"
        borderWidth={1}
        borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
        medianWidth={2}
        medianColor={{ from: "color", modifiers: [["darker", 1.6]] }}
        whiskerWidth={2}
        whiskerEndSize={0.5}
        whiskerColor={{ from: "color", modifiers: [["darker", 0.8]] }}
        markers={markers}
        theme={chartTheme}
        /* No spring. With one axis locked across reaches a two-point box is
           a fraction of a pixel tall, and every nivo preset with any bounce
           dipped below zero mid-animation, which the browser reports as an
           invalid negative rect height. A config object would fix it but the
           boxplot package types motionConfig as a string, so the chart just
           jumps to its final geometry. */
        animate={false}
        axisBottom={{
          tickSize: 6,
          tickPadding: 6,
          tickRotation: -45,
          legend: "Upstream to downstream",
          legendPosition: "middle",
          legendOffset: 68,
        }}
        axisLeft={{
          tickSize: 6,
          tickPadding: 6,
          legend: unit || parameter?.paramAbbrev || "",
          legendPosition: "middle",
          legendOffset: -60,
        }}
        tooltip={renderTooltip}
      />
    </Box>
  );
}
